<template>
    <DModal
        :show="showModal"
        :title="modalTitle"
        is-big
        overflow-auto
        @close="closeModal"
    >
        <div
            v-if="emailLoading"
            class="loading-box"
        >
            <DIcon
                name="loading"
                class="loading"
                :size="40"
            />
        </div>

        <div
            v-if="!emailLoading"
            class="modal-base"
        >
            <div
                v-if="errors.length === 0"
                class="modal-header"
            >
                <DTabs>
                    <DTabItem
                        :label="__('tools.emails.modal.tabs.html')"
                        :active="currentTab === 0"
                        @click="changeTab(0)"
                    />
                    <DTabItem
                        :label="__('tools.emails.modal.tabs.plain_text')"
                        :active="currentTab === 1"
                        @click="changeTab(1)"
                    />
                    <DTabItem
                        :label="__('tools.emails.modal.tabs.send_copy')"
                        :active="currentTab === 2"
                        @click="changeTab(2)"
                    />
                </DTabs>
                <DButton
                    v-if="emailArchive.eml"
                    icon="download"
                    :label="__('customers.communications.modal.download')"
                    :disabled="isInLoadingState"
                    @click="downloadSampleDocument()"
                />
            </div>

            <DAlert
                v-if="errors.length > 0"
                color="danger"
                class="d-flex align-items-center"
            >
                <DIcon
                    name="alert"
                    :size="24"
                />
                <div class="pr-2">
                    <p
                        v-for="(error, index) in errors"
                        :key="index"
                        class="mb-0"
                    >
                        {{ error }}
                    </p>
                </div>
            </DAlert>

            <div v-if="errors.length === 0">
                <DCard
                    v-if="currentTab === 0"
                    class="ignore-parent-styles"
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <div v-html="emailArchive.html" />
                    <!--eslint-enable-->
                </DCard>

                <DCard
                    v-if="currentTab === 1"
                    padding
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        class="raw-textbox"
                        v-html="emailArchive.text"
                    />
                    <!--eslint-enable-->
                </DCard>

                <DCard
                    v-if="currentTab === 2"
                    padding
                >
                    <div class="mt-3">
                        <DInput
                            id="email"
                            v-model="email"
                            :label="__('tools.emails.modal.email')"
                            add-label-classes="required"
                        />
                        <div class="d-flex justify-content-start align-items-center">
                            <DButton
                                :loading="sendingEmailLoading"
                                :label="__('customers.communications.modal.tabs.send_copy')"
                                :disabled="!email || isInLoadingState"
                                :color="'primary'"
                                @click="sendEmail()"
                            />
                            <p
                                v-if="mailSentSuccessfully"
                                class="text-success ms-3 mb-0 p-0"
                            >
                                {{ __('tools.emails.modal.mail_sent') }}
                            </p>
                        </div>
                    </div>
                </DCard>
            </div>
        </div>
    </DModal>
</template>

<script>
/* eslint vue/no-v-html: 0 */
import { __ } from '~@/Composables/translation'

export default {
    name: 'ShowEmailModal',
    props: {
        showModal: {
            type: Boolean,
            default: false
        },
        emailId: {
            type: Number,
            default: 0
        },
        customer: {
            type: Object,
            default: () => { return {} }
        }
    },
    emits: ['closeModal'],
    data () {
        return {
            emailArchive: {},
            modalTitle: __('messages.loading'),
            emailLoading: true,
            sendingEmailLoading: false,
            mailSentSuccessfully: false,
            errors: [],
            email: this.customer.email ?? '',
            currentTab: 0
        }
    },
    watch: {
        showModal (show, oldShow) {
            if (show && !oldShow) {
                // modal was opened
                this.loadEmailData()
            }
        }
    },
    methods: {
        changeTab (tabIndex) {
            this.currentTab = tabIndex
        },
        loadEmailData () {
            this.emailLoading = true
            this.emailArchive = {}
            this.errors = []

            window.axios.get(this.$route('customers.customers.email-archives.show', {
                customer: this.customer.id,
                email_archive: this.emailId
            }))
                .then(response => {
                    this.emailArchive = response.data
                    this.modalTitle = this.emailArchive.subject
                })
                .catch((error) => {
                    this.parseErrors(error)
                })
                .finally(() => {
                    this.emailLoading = false
                })
        },
        downloadSampleDocument () {
            const link = document.createElement('a')
            link.download = this.emailArchive.fileName
            const blob = new Blob([this.emailArchive.eml], {
                type: 'text/plain'
            })
            link.href = window.URL.createObjectURL(blob)
            link.click()
        },
        sendEmail () {
            this.sendingEmailLoading = true
            this.mailSentSuccessfully = false
            this.errors = []

            window.axios.post(this.$route('customers.customers.email-archives.resend', {
                customer: this.customer.id,
                email_archive: this.emailId
            }), {
                email: this.email
            })
                .then(() => {
                    this.mailSentSuccessfully = true
                })
                .catch((error) => {
                    this.parseErrors(error)
                })
                .finally(() => {
                    this.sendingEmailLoading = false
                })
        },
        closeModal () {
            this.errors = []
            this.email = this.customer.email ?? ''
            this.modalTitle = __('messages.loading')
            this.mailSentSuccessfully = false
            this.sendingEmailLoading = false
            this.emailLoading = true
            this.currentTab = 0

            this.$emit('closeModal')
        },
        parseErrors (error) {
            const errorObject = error.response && error.response.data && error.response.data.errors
                ? error.response.data.errors
                : {}
            Object.keys(errorObject).forEach((field) => {
                errorObject[field].forEach((message) => {
                    this.errors.push(message)
                })
            })
        }
    }
}
</script>
<style lang="scss" scoped>
.modal-base {
    margin: 15px;
    display: grid;
    grid-gap: 15px;

    & .modal-header {
        display: grid;
        grid-gap: 15px;
        grid-template-columns: 1fr auto;
        align-items: center;
    }
    & .raw-textbox {
        font-family: monospace, monospace;
        white-space: pre-wrap;
    }
}

.loading-box {
    height: 250px;
    display: flex;
    justify-content: center;
    align-items: center;

    & .loading {
        animation: loading 0.4s linear infinite;
    }
}

@keyframes loading {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
